import { ScriptDescriptionType } from '../Types';
import BaseViewModel from '../Models/BaseViewModel';
import BaseScript, {
	ConstantScript,
	EnumTitleScript,
	HashtableScript,
	JavaScript,
	RegexpScript,
	RubyScript,
	ValidationScript,
	SelectDictDataScript,
	SearchAttributeScript,
} from './ScriptTypes';
import { PathUtil } from './Utils/PathUtil';

export type SetPropertyValueHandler = (
	element: BaseViewModel,
	propertyName: string,
	value: any,
) => void;
export type GetPropertyValueHandler = (
	element: BaseViewModel,
	propertyName: string,
) => any;
export type RunScriptHandler = (
	element: BaseViewModel,
	scriptDescription?: BaseScript,
	purpose?: string,
) => any;
export type SetValueHandler = (propertyName: string, value: any) => void;

type DepObjectType = {
	[ScriptType: string]: {
		[FieldName: string]: Array<BaseViewModel>;
	};
	ValidationScript: {
		[modelPath: string]: Array<BaseViewModel>;
	};
};

type ElementWithChildren = BaseViewModel & {
	PageIndex?: number;
	FormElements?: BaseViewModel[];
	Pages?: BaseViewModel[];
	CollectionItems?: BaseViewModel[];
	PageElements?: BaseViewModel[];
};

class ScriptMediator {
	inited = false;
	isInitializing = false;
	// текущая страница (для скриптов на шаге)
	currentPageIndex: number | null = null;
	// какие страницы уже инициализировали скрипты
	initializedPageIndexes: Set<number> = new Set();

	viewModel: BaseViewModel | any;
	InitNotValueScriptTypes: Array<string>;
	ScriptTypes: Array<string>;

	registeredScripts: DepObjectType = { ValidationScript: {} };
	registeredConditionalScripts: any = {};

	registerDelayed: Array<any> = [];

	/** используется для защиты от рекурсивных вызовов одного и того же скрипта */
	runningScripts: Set<string> = new Set();

	onSetPropertyValue?: SetPropertyValueHandler;
	onSetValue?: SetValueHandler;
	runScript?: RunScriptHandler;

	constructor(isReadOnly = false) {
		this.inited = false;
		BaseViewModel.isReadOnly = isReadOnly;
		if (isReadOnly) {
			// В режиме readonly создаём также DefaultValue/Calculation, чтобы вычислить их при инициализации
			this.ScriptTypes = [
				'DisplayScript',
				'DefaultValueScript',
				'CalculationScript',
			];
			this.InitNotValueScriptTypes = [
				'DisplayScript',
				'DefaultValueScript',
				'CalculationScript',
			];
		} else {
			this.InitNotValueScriptTypes = [
				'MaxItemsCountScript',
				'DisplayScript',
				'EnabledScript',
				'MandatoryScript',
			];

			this.ScriptTypes = [
				'MaxItemsCountScript',
				'DefaultValueScript',
				'CalculationScript',
				'DisplayScript',
				'EnabledScript',
				'MandatoryScript',
				'MaxDateScript',
				'MinDateScript',
			];
		}
	}

	/**
	 * Устанавливает значение поля из внутренних процессов
	 * Так же вызывает внешнею функцию - onSetPropertyValue
	 * @param element Элемент формы
	 * @param propertyName Наименование свойства поля
	 * @param value Значение поля
	 */
	setPropertyValue: SetPropertyValueHandler = (
		element,
		propertyName,
		value,
	) => {
		if (!element) {
			throw new Error('Mediator.setPropertyValue - Element = null!');
		}
		const el = element as any;
		el[propertyName] = value;

		if (propertyName === 'Value') {
			if (element.setValue) {
				element.setValue(value);
				this.valueChanged(element, value);
			}
			if (this.onSetValue) {
				this.onSetValue(element.FullPath, value);
			}
		}

		if (this.onSetPropertyValue) {
			this.onSetPropertyValue(element, propertyName, value);
		}

		try {
			const eModel: any =
				BaseViewModel.EntityPathToViewModel[element.FullPath];
			if (eModel) {
				eModel[propertyName] = value;
			}
		} catch (e: any) {
			const registeredCount = Object.keys(
				BaseViewModel.EntityPathToViewModel || {},
			).length;
			console.warn('ScriptMediator.setPropertyValue error', {
				fullPath: element.FullPath,
				propertyName,
				registeredEntities: registeredCount,
				error: e?.message || e,
			});
		}
	};

	/**
	 * Применяет к коллекции результат MaxItemsCountScript: сначала убирает элементы сверх нового лимита,
	 * и только потом проставляет само свойство, чтобы к моменту перерисовки формы коллекция уже была обрезана
	 */
	applyMaxItemsCount = (element: BaseViewModel, value: unknown) => {
		const collection = element as BaseViewModel & {
			trimItemsToMaxCount?: (maxItemsCount: number) => void;
		};
		const maxItemsCount = parseInt(String(value), 10);
		if (
			typeof collection.trimItemsToMaxCount === 'function' &&
			!Number.isNaN(maxItemsCount)
		) {
			collection.trimItemsToMaxCount(maxItemsCount);
		}
		this.setPropertyValue(element, 'MaxItemsCount', value);
	};

	/**
	 * Функция будет вызвана внешним компонентом для внесения изменения значения поля в скрипты
	 * @param fieldName Наименование поля
	 * @param value Значение поля
	 */
	changeValue = (fieldName: string, value: any) => {
		const path = fieldName.replace('_.', '');
		const element = BaseViewModel.EntityPathToViewModel[path];
		if (!element) {
			const registeredCount = Object.keys(
				BaseViewModel.EntityPathToViewModel || {},
			).length;
			console.warn('Не найдена модель формы!', {
				fieldName,
				path,
				registeredEntities: registeredCount,
			});
			return;
		}
		this.valueChanged(element, value);
	};

	/**
	 * Зарегистрировать скрипт
	 */
	registerScript = (scriptModel: BaseScript) => {
		this.registerScriptByFieldsDeps(scriptModel);
		this.registerScriptByConditionalDeps(scriptModel);
		return scriptModel;
	};

	/**
	 * зарегать скрипт для выполнения при изменении значений зависимых полей
	 * (запуск скрипта осуществится при выполнении regex на путь элемента изменённого значения)
	 */
	registerScriptByConditionalDeps = (scriptModel: BaseScript) => {
		let dependentRegexStrs = scriptModel.getDependentDynFieldRegexStrs();
		if (scriptModel.LaunchCondition) {
			dependentRegexStrs = dependentRegexStrs.concat(
				scriptModel.LaunchCondition.getDependentDynFieldRegexStrs(),
			);
		}

		dependentRegexStrs.forEach((depRegex: any) => {
			if (!this.registeredConditionalScripts[scriptModel.Purpose]) {
				this.registeredConditionalScripts[scriptModel.Purpose] = {};
			}
			if (
				this.registeredConditionalScripts[scriptModel.Purpose][
					depRegex
				] === undefined
			) {
				this.registeredConditionalScripts[scriptModel.Purpose][
					depRegex
				] = [];
			}

			this.registeredConditionalScripts[scriptModel.Purpose][
				depRegex
			].push(scriptModel.contextElement);
		});
	};

	/**
	 * зарегать скрипт для выполнения при изменении значений зависимых полей
	 * (для заранее указанных статических путей)
	 */
	registerScriptByFieldsDeps = (scriptModel: BaseScript) => {
		let dependentElements = scriptModel.getDependentFields();

		//добавим зависимость для LaunchCondition
		if (scriptModel.LaunchCondition) {
			dependentElements = dependentElements.concat(
				scriptModel.LaunchCondition.getDependentFields(),
			);
		}

		dependentElements.forEach((dependentElementPath: any) => {
			const dependentElement = this.getViewModelByPath(
				scriptModel.contextElement,
				dependentElementPath,
			);
			if (dependentElement) {
				const dependentElementFullPath = dependentElement.FullPath;
				this.addDependency(
					scriptModel.contextElement,
					dependentElementFullPath,
					scriptModel,
				);
			} else {
				console.groupCollapsed(
					"Can't find dependent element for script",
				);
				console.log(scriptModel.Content);
				console.groupEnd();
			}
		});
	};

	getViewModelByPath = (element: BaseViewModel, path: string) => {
		const absolutePath = PathUtil.toAbsolutePath(path, element.FullPath);
		return BaseViewModel.EntityPathToViewModel[absolutePath];
	};

	/*
	 * Добавить зависимость
	 */
	addDependency = (
		contextElement: BaseViewModel,
		dependentItemPath: string,
		scriptModel: BaseScript,
	) => {
		if (!this.registeredScripts[scriptModel.Purpose]) {
			this.registeredScripts[scriptModel.Purpose] = {};
		}
		if (
			this.registeredScripts[scriptModel.Purpose][dependentItemPath] ===
			undefined
		) {
			this.registeredScripts[scriptModel.Purpose][dependentItemPath] = [];
		}

		this.registeredScripts[scriptModel.Purpose][dependentItemPath].push(
			contextElement,
		);
	};

	/**
	 * Вся модель построена
	 */
	onViewModelBuilded = (viewModel: BaseViewModel) => {
		if (viewModel.Path === '_' && !viewModel.Parent) {
			this.viewModel = viewModel;
		}

		// регистрируем отложенные скрипты
		this.registerDelayed.forEach((item: any) => {
			this.registerScript(item);
		});

		this.registerDelayed = [];
		this.inited = true;
		this.isInitializing = true;
		this.runDefaultScripts(viewModel);
		this.isInitializing = false;
		if (this.currentPageIndex !== null) {
			this.initializedPageIndexes.add(this.currentPageIndex);
		}
	};

	// прогоняем DisplayScript по всем страницам один раз на старте
	initPagesVisibility = () => {
		const root = this.viewModel as ElementWithChildren | null;
		const container = root?.FormElements?.[0] as
			(BaseViewModel & { Pages?: BaseViewModel[] }) | undefined;
		if (!container || !Array.isArray(container.Pages)) {
			return;
		}
		container.Pages.forEach((page: BaseViewModel) => {
			if (page?.DisplayScript && this.canRunScriptType('DisplayScript')) {
				this.runScriptOnElement(
					page,
					page.DisplayScript,
					'DisplayScript',
				);
			}
		});
	};

	// обновляем активную страницу и инициализируем ее скрипты
	setCurrentPageIndex = (pageIndex: number | null) => {
		this.currentPageIndex = pageIndex;
		if (
			this.viewModel &&
			pageIndex !== null &&
			!this.initializedPageIndexes.has(pageIndex)
		) {
			const page = this.getPageByIndex(pageIndex);
			if (page) {
				this.isInitializing = true;
				this.runDefaultScripts(page);
				this.isInitializing = false;
				this.initializedPageIndexes.add(pageIndex);
			}
		}
	};

	// получаем страницу по индексу из контейнера
	getPageByIndex = (pageIndex: number) => {
		const root = this.viewModel as ElementWithChildren | null;
		const container = root?.FormElements?.[0] as
			(BaseViewModel & { Pages?: BaseViewModel[] }) | undefined;
		if (!container || !Array.isArray(container.Pages)) {
			return null;
		}
		return container.Pages[pageIndex] ?? null;
	};

	RunDefaultValueInitializationScript = (element: BaseViewModel) => {
		const isEmpty =
			element.Value === undefined ||
			element.Value === null ||
			element.Value === '';
		if (isEmpty) {
			let defaultValue = undefined;
			if (
				element.DefaultValueScript &&
				this.canRunScriptType('DefaultValueScript') &&
				element.DefaultValueScript.canLaunchScript()
			) {
				defaultValue = element.DefaultValueScript.runScript();
			} else {
				if (
					element.CalculationScript &&
					this.canRunScriptType('CalculationScript') &&
					element.CalculationScript.canLaunchScript()
				) {
					defaultValue = element.CalculationScript.runScript();
				}
			}
			if (defaultValue !== null && defaultValue !== undefined) {
				this.setPropertyValue(element, 'Value', defaultValue);
			}
		}
	};

	RunInitializationScripts = (element: BaseViewModel) => {
		this.RunDefaultValueInitializationScript(element);

		this.getInitNotValueScriptTypes().forEach((scriptType: any) => {
			const scriptDescr = (element as any)[scriptType];
			if (scriptDescr) {
				if (scriptType === 'CalculationScript') {
					const hasValue =
						element.Value !== undefined &&
						element.Value !== null &&
						element.Value !== '';
					if (hasValue) {
						return;
					}
				}
				this.runScriptOnElement(element, scriptDescr, scriptType);
			}
		});
	};

	runDefaultScripts = (element: BaseViewModel) => {
		const el = element as ElementWithChildren;
		if (
			this.currentPageIndex !== null &&
			el?.PageIndex !== undefined &&
			el.PageIndex !== this.currentPageIndex
		) {
			return;
		}

		if (element.MaxDateScript && this.canRunScriptType('MaxDateScript')) {
			const value = element.MaxDateScript.runScript();
			this.setPropertyValue(element, 'MaxValue', value);
		}

		if (element.MinDateScript && this.canRunScriptType('MinDateScript')) {
			const value = element.MinDateScript.runScript();
			this.setPropertyValue(element, 'MinValue', value);
		}

		this.RunInitializationScripts(element);

		// var elements = element.FormElements || element.Pages || ko.utils.unwrapObservable(element.CollectionItems) || element.PageElements;
		// TODO: Как-то некрасиво(
		const elements =
			el.FormElements ||
			el.Pages ||
			el.CollectionItems ||
			el.PageElements;
		if (elements) {
			elements.forEach((child: any) => {
				this.runDefaultScripts(child);
			});
		}
	};

	/**
	 * Создать объект скрипта по дескриптору
	 */
	createScriptObject = (
		elementViewModel: BaseViewModel,
		scriptDescriptor: ScriptDescriptionType,
		scriptType: string,
	) => {
		const script = this.prepareScript(
			elementViewModel,
			scriptDescriptor,
			scriptType,
		);
		this.registerDelayed.push(script);
		return script;
	};

	prepareScript = (
		elementViewModel: BaseViewModel,
		scriptDescriptor: ScriptDescriptionType,
		scriptType: string,
	): BaseScript => {
		let scriptModel;
		switch (scriptDescriptor.ScriptType) {
			case 'Special':
				if (scriptDescriptor.Content.startsWith('Validator')) {
					scriptModel = new ValidationScript(
						elementViewModel,
						scriptDescriptor,
						scriptType,
					);
				} else if (
					scriptDescriptor.Content.indexOf('EnumTitle:') >= 0
				) {
					scriptModel = new EnumTitleScript(
						elementViewModel,
						scriptDescriptor,
						scriptType,
					);
				} else if (
					scriptDescriptor.Content.indexOf('HashTable:') >= 0
				) {
					scriptModel = new HashtableScript(
						elementViewModel,
						scriptDescriptor,
						scriptType,
					);
				} else if (
					scriptDescriptor.Content.indexOf('SearchAttribute:') >= 0
				) {
					scriptModel = new SearchAttributeScript(
						elementViewModel,
						scriptDescriptor,
						scriptType,
					);
				} else if (
					scriptDescriptor.Content.indexOf('SelectDictData:') >= 0
				) {
					scriptModel = new SelectDictDataScript(
						elementViewModel,
						scriptDescriptor,
						scriptType,
					);
				} else {
					console.warn(
						'Special is not implemented. script.Content is null',
					);
					scriptModel = null;
				}
				break;
			case 'DynamicExpresso':
			case 'IronRuby':
				scriptModel = new RubyScript(
					elementViewModel,
					scriptDescriptor,
					scriptType,
				);
				break;
			case 'Regex':
				scriptModel = new RegexpScript(
					elementViewModel,
					scriptDescriptor,
					scriptType,
				);
				break;
			case 'Constant':
				scriptModel = new ConstantScript(
					elementViewModel,
					scriptDescriptor,
					scriptType,
				);
				break;
			default:
				scriptModel = new JavaScript(
					elementViewModel,
					scriptDescriptor,
					scriptType,
				);
				break;
		}
		if (scriptModel && scriptDescriptor.LaunchCondition) {
			scriptModel.LaunchCondition = this.prepareScript(
				elementViewModel,
				scriptDescriptor.LaunchCondition,
				'LaunchCondition',
			);
		}
		return (
			scriptModel ??
			new BaseScript(elementViewModel, scriptDescriptor, scriptType)
		);
	};

	getConditionalScriptElementsForPath = (fullPath: any, scriptType: any) => {
		let needToRunElements: any[] = [];
		if (this.registeredConditionalScripts[scriptType]) {
			const scriptsForType =
				this.registeredConditionalScripts[scriptType];
			for (const regex in scriptsForType) {
				if (scriptsForType.hasOwnProperty(regex)) {
					try {
						// eslint-disable-next-line
						const escRegex = regex.trim().replace('\s+', '');
						const Reg = new RegExp(escRegex);
						if (Reg.test(fullPath)) {
							needToRunElements = needToRunElements.concat(
								scriptsForType[regex],
							);
						}
					} catch (e) {}
				}
			}
		}
		return needToRunElements;
	};

	/**
	 * Поменялось значение модели
	 */
	valueChanged = (model: BaseViewModel, value: any) => {
		if (!this.inited) {
			return;
		}

		model.Value = value;
		const registeredModel =
			BaseViewModel.EntityPathToViewModel[model.FullPath];
		if (registeredModel) {
			registeredModel.Value = value;
		}

		// запускаем зависимые скрипты
		const modelPath = model.FullPath;

		this.getScriptTypes().forEach((scriptType) => {
			const regScriptsObj = this.registeredScripts[scriptType];
			if (regScriptsObj) {
				const regScripts = regScriptsObj[modelPath];
				if (regScripts) {
					regScripts.forEach((element) => {
						const script = (element as any)[scriptType];
						// выполняем скрипты для элемента
						if (script) {
							this.runScriptOnElement(
								element,
								script,
								scriptType,
							);
						}
					});
				}
			}

			// выполнение скриптов для "нестатических" путей, например путь до элемента коллекции
			if (this.registeredConditionalScripts[scriptType]) {
				const needToRunElements =
					this.getConditionalScriptElementsForPath(
						modelPath,
						scriptType,
					);
				needToRunElements.forEach((element: any) => {
					if (element[scriptType]) {
						this.runScriptOnElement(
							element,
							element[scriptType],
							scriptType,
						);
					}
				});
			}
		});

		// запускаем зависимые скрипты валидации (не во время инициализации DefaultValueScript)
		if (!this.isInitializing) {
			if (
				this.registeredScripts.ValidationScript &&
				this.registeredScripts.ValidationScript[modelPath]
			) {
				this.registeredScripts.ValidationScript[modelPath].forEach(
					(element: BaseViewModel) => {
						// выполняем скрипты для элемента
						if (element.ValidationScripts) {
							element.ValidationScripts.forEach(
								(script: BaseScript) => {
									this.runScriptOnElement(
										element,
										script,
										'ValidationScript',
									);
								},
							);
						}
					},
				);
			}

			if (this.registeredConditionalScripts.ValidationScript) {
				const needToRunElements =
					this.getConditionalScriptElementsForPath(
						modelPath,
						'ValidationScript',
					);
				needToRunElements.forEach((element: any) => {
					if (element.ValidationScripts) {
						element.ValidationScripts.forEach(
							(script: BaseScript) => {
								this.runScriptOnElement(
									element,
									script,
									'ValidationScript',
								);
							},
						);
					}
				});
			}

			model.IsValid = model.Validate();
			// Уведомляем React о смене валидности, иначе состояние формы
			// не обновится до следующего изменения, идущего через setPropertyValue
			this.onSetPropertyValue?.(model, 'IsValid', model.IsValid);
		}
	};

	runScriptOnElement = (
		element: BaseViewModel,
		script: BaseScript,
		scriptType: string,
	) => {
		const key = `${element.FullPath}|${scriptType}`;
		if (this.runningScripts.has(key)) {
			return;
		}

		this.runningScripts.add(key);

		//LaunchCondition
		const canLaunch = script.canLaunchScript() === true;

		if (canLaunch) {
			let result = script.runScript();

			// TODO В режиме readonly не отрабатывают скрипты видимости, поэтому здесь костыль
			if (
				(result === null || result === undefined) &&
				element.DisplayScript
			) {
				const endField =
					element.FullPath.split('.')[
						element.FullPath.split('.').length - 1
					];
				if (
					element.DisplayScript?.Content?.includes(
						'FormObject.ContainsKey("',
					)
				) {
					const reg = new RegExp(/(?<=ContainsKey\(")[^"]*/i);
					const field = reg.exec(element.DisplayScript.Content);
					if (field) {
						result =
							BaseViewModel.EntityPathToViewModel[
								element.FullPath.replace(endField, field[0])
							]?.Value;
						if (result === undefined) {
							//Виды сведений иногда приходят к нам с кириллическими символами, спасибо им за это большое
							const replaceCyrillic = (input: any) => {
								const cyrillicMap: any = {
									Е: 'E',
									е: 'e',
									Т: 'T',
									у: 'y',
									О: 'O',
									о: 'o',
									Р: 'P',
									р: 'p',
									А: 'A',
									а: 'a',
									Н: 'H',
									К: 'K',
									Х: 'X',
									х: 'x',
									С: 'C',
									с: 'c',
									В: 'B',
									М: 'M',
								};
								return input.replace(
									/[А-Яа-я]/g,
									(match: any) => {
										return cyrillicMap[match] || match;
									},
								);
							};
							result = BaseViewModel.EntityPathToViewModel[
								replaceCyrillic(
									element.FullPath.replace(
										endField,
										field[0],
									),
								)
							]?.Value
								? true
								: false;
						}
					}
				}
			}

			switch (scriptType) {
				case 'CalculationScript':
					if (result !== '' && this.isFloat(result)) {
						result = this.striptFLoat(result);
						this.setPropertyValue(element, 'Value', result);
					} else {
						this.setPropertyValue(element, 'Value', result);
					}
					break;
				case 'DisplayScript':
					this.setPropertyValue(element, 'ControlVisibility', result);
					break;
				case 'EnabledScript':
					this.setPropertyValue(element, 'ControlEnabling', result);
					break;
				case 'MandatoryScript':
					this.setPropertyValue(element, 'IsMandatory', result);
					// TODO
					const el = element as any;
					if (
						!!el.CollectionItems &&
						el.CollectionItems.length === 0 &&
						result === true
					) {
						el.addItemOnServer();
					}
					break;
				case 'ValidationScript':
					this.setPropertyValue(element, 'IsValid', !result);
					this.setPropertyValue(
						element,
						'errorMessage',
						result !== true ? script.Message : 'Не верно',
					);
					break;
				case 'MaxItemsCountScript':
					this.applyMaxItemsCount(element, result);
					break;
				case 'MaxDateScript':
					this.setPropertyValue(element, 'MaxValue', result);
					break;
				case 'MinDateScript':
					this.setPropertyValue(element, 'MinValue', result);
					break;

				default:
					break;
			}
		} else {
			switch (scriptType) {
				case 'DisplayScript':
					this.setPropertyValue(element, 'ControlVisibility', true);
					break;

				case 'EnabledScript':
					this.setPropertyValue(element, 'ControlEnabling', true);
					break;

				case 'MandatoryScript':
					this.setPropertyValue(element, 'IsMandatory', false);
					break;
				case 'ValidationScript':
					this.setPropertyValue(element, 'IsValid', true);
					this.setPropertyValue(element, 'errorMessage', '');
					break;
				case 'MaxItemsCountScript':
					this.applyMaxItemsCount(element, 0);
					break;

				default:
					break;
			}
		}

		this.runningScripts.delete(key);
	};

	setReadOnly = () => {
		// В режиме readonly оставляем выполнение DisplayScript, а также
		// формирование значений по DefaultValue/Calculation, чтобы значения были видимы
		this.ScriptTypes = [
			'DisplayScript',
			'DefaultValueScript',
			'CalculationScript',
		];
		this.InitNotValueScriptTypes = [
			'DisplayScript',
			'DefaultValueScript',
			'CalculationScript',
		];
	};

	getInitNotValueScriptTypes = () => {
		return this.InitNotValueScriptTypes;
	};
	getScriptTypes = () => {
		return this.ScriptTypes;
	};
	canRunScriptType = (scriptType: string) => {
		return this.getScriptTypes().indexOf(scriptType) >= 0;
	};

	striptFLoat = (newValue: string) => {
		if (newValue) {
			const float = parseFloat(newValue);
			if (!isNaN(float)) {
				const precision = 12;
				const presioned = parseFloat(float.toFixed(precision));
				if (!isNaN(presioned) && presioned !== float) {
					return presioned;
				}
			}
		}
		return newValue;
	};

	isFloat = (n: any): boolean => {
		return Number(n) === n && n % 1 !== 0;
	};
}

export default ScriptMediator;
